import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Loader2, Copy, Printer, ExternalLink } from "lucide-react";
import {
  PageContainer,
  PageHeader,
  Card,
  SectionTitle,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { QR_MENU_THEMES, qrMenuBrowseUrl } from "@/lib/qrMenuThemes";
import {
  QrMenuCardPreview,
  QrMenuCardPrint,
  printQrMenuCards,
} from "@/components/qr/QrMenuCardPrint";
import { useTranslation } from "react-i18next";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[12px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

function Toggle({ label, hint, checked, onCheckedChange }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b last:border-b-0">
      <div>
        <div className="text-[12.5px] font-medium text-slate-800">{label}</div>
        {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <Switch checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

export default function RestaurantSetup() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const branchId = user?.default_branch?.id;
  const branchCode = user?.default_branch?.code;
  const branchName = user?.default_branch?.name || "Restaurant";
  const [form, setForm] = useState(null);
  const [slotDraft, setSlotDraft] = useState([]);
  const [stationDraft, setStationDraft] = useState([]);

  const { data, isLoading } = useQuery({
    queryKey: ["restaurant-settings", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/restaurant-settings", { params: { branch_id: branchId } });
      return res.settings;
    },
    enabled: Boolean(branchId),
  });

  const { data: fastMenuData, isLoading: fastMenuLoading } = useQuery({
    queryKey: ["fast-menu-slots", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/fast-menu-slots", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const { data: kitchenStationsData, isLoading: stationsLoading } = useQuery({
    queryKey: ["kitchen-stations", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/kitchen-stations", { params: { branch_id: branchId } });
      return res;
    },
    enabled: Boolean(branchId),
  });

  const { data: kitchenPrinters = [] } = useQuery({
    queryKey: ["kitchen-printers", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/printers", {
        params: { branch_id: branchId, type: "Kitchen" },
      });
      return res.printers || [];
    },
    enabled: Boolean(branchId),
  });

  const { data: menuItems = [] } = useQuery({
    queryKey: ["fast-menu-items", branchId],
    queryFn: async () => {
      const { data: res } = await api.get("/menu-items", { params: { branch_id: branchId, show_in_pos: 1 } });
      return (res.menu_items || []).filter((m) => m.show_in_pos !== false && m.status === "Active");
    },
    enabled: Boolean(branchId),
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  useEffect(() => {
    if (kitchenStationsData?.stations) {
      setStationDraft(
        kitchenStationsData.stations.map((s) => ({
          code: s.code,
          name: s.name,
          printer_id: s.printer_id ? String(s.printer_id) : "",
          is_active: s.is_active !== false,
        })),
      );
    }
  }, [kitchenStationsData]);

  useEffect(() => {
    if (fastMenuData?.slots) {
      setSlotDraft(
        fastMenuData.slots.map((s) => ({
          slot_key: s.slot_key,
          menu_item_id: s.menu_item_id ? String(s.menu_item_id) : "",
        })),
      );
    }
  }, [fastMenuData]);

  const saveMutation = useMutation({
    mutationFn: async () => api.put("/restaurant-settings", { settings: form }, { params: { branch_id: branchId } }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["restaurant-settings", branchId] });
      toast.success("Restaurant settings saved");
    },
    onError: () => toast.error("Failed to save settings"),
  });

  const saveStationsMutation = useMutation({
    mutationFn: async () => {
      const stations = stationDraft.map((s) => ({
        code: s.code,
        printer_id: s.printer_id ? Number(s.printer_id) : null,
        is_active: s.is_active,
      }));
      return api.put("/kitchen-stations", { stations }, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kitchen-stations", branchId] });
      toast.success("Kitchen station routing saved");
    },
    onError: () => toast.error("Failed to save station routing"),
  });

  const saveFastMenuMutation = useMutation({
    mutationFn: async () => {
      const slots = slotDraft
        .filter((s) => s.menu_item_id)
        .map((s, index) => ({
          slot_key: s.slot_key,
          menu_item_id: Number(s.menu_item_id),
          sort_order: index + 1,
        }));
      return api.put("/fast-menu-slots", { slots }, { params: { branch_id: branchId } });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fast-menu-slots", branchId] });
      toast.success("Fast menu slots saved");
    },
    onError: () => toast.error("Failed to save fast menu slots"),
  });

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  if (isLoading || !form) {
    return (
      <PageContainer>
        <div className="h-40 grid place-items-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader
        title="Restaurant Setup"
        subtitle={`Service hours, POS preferences — ${user?.default_branch?.name || "branch"}`}
        actions={
          <Button
            size="sm"
            className="h-9 bg-[#16A34A] hover:bg-emerald-700"
            data-testid="rest-save"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
          >
            {saveMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
            Save Changes
          </Button>
        }
      />

      <Tabs defaultValue="hours" className="w-full">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-brand-cream-panel/80 p-1 border border-[#E8D5C8]">
          <TabsTrigger
            value="hours"
            className="text-[12.5px] data-[state=active]:bg-white data-[state=active]:text-brand-orange data-[state=active]:shadow-sm"
          >
            Hours &amp; Ramadan
          </TabsTrigger>
          <TabsTrigger
            value="qr"
            className="text-[12.5px] data-[state=active]:bg-white data-[state=active]:text-brand-orange data-[state=active]:shadow-sm"
          >
            QR Menu
          </TabsTrigger>
          <TabsTrigger
            value="pos"
            className="text-[12.5px] data-[state=active]:bg-white data-[state=active]:text-brand-orange data-[state=active]:shadow-sm"
          >
            POS &amp; Kitchen
          </TabsTrigger>
          <TabsTrigger
            value="delivery"
            className="text-[12.5px] data-[state=active]:bg-white data-[state=active]:text-brand-orange data-[state=active]:shadow-sm"
          >
            Delivery
          </TabsTrigger>
        </TabsList>

        <TabsContent value="hours" className="mt-0 space-y-4 focus-visible:ring-0">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="p-5">
            <SectionTitle>Service Hours</SectionTitle>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Opening Time">
                <Input type="time" value={form.opening_time} onChange={(e) => setField("opening_time", e.target.value)} className="h-9" />
              </Field>
              <Field label="Closing Time">
                <Input type="time" value={form.closing_time} onChange={(e) => setField("closing_time", e.target.value)} className="h-9" />
              </Field>
              <Field label="Lunch Hours Start">
                <Input type="time" value={form.lunch_start} onChange={(e) => setField("lunch_start", e.target.value)} className="h-9" />
              </Field>
              <Field label="Dinner Hours Start">
                <Input type="time" value={form.dinner_start} onChange={(e) => setField("dinner_start", e.target.value)} className="h-9" />
              </Field>
              <Field label="Order Type Default">
                <Select value={form.default_order_type} onValueChange={(v) => setField("default_order_type", v)}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DI">Dine In</SelectItem>
                    <SelectItem value="TA">Take Away</SelectItem>
                    <SelectItem value="DL">Delivery</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Field label="Service Charge %">
                <Input value={form.service_charge_percent} onChange={(e) => setField("service_charge_percent", Number(e.target.value))} type="number" className="h-9 text-right" />
              </Field>
            </div>
          </Card>

          <Card className="p-5">
            <SectionTitle>Ramadan Mode</SectionTitle>
            <Toggle
              label="Enable Ramadan mode"
              hint="Show banner on dashboard/POS and optional adjusted hours"
              checked={Boolean(form.ramadan_mode_enabled)}
              onCheckedChange={(v) => setField("ramadan_mode_enabled", v)}
            />
            <Toggle
              label="Pre-order only during fasting"
              checked={Boolean(form.ramadan_preorder_only)}
              onCheckedChange={(v) => setField("ramadan_preorder_only", v)}
            />
            <div className="grid grid-cols-2 gap-3 mt-2">
              <Field label="Ramadan open">
                <Input type="time" value={form.ramadan_opening_time || ""} onChange={(e) => setField("ramadan_opening_time", e.target.value)} className="h-9" />
              </Field>
              <Field label="Ramadan close">
                <Input type="time" value={form.ramadan_closing_time || ""} onChange={(e) => setField("ramadan_closing_time", e.target.value)} className="h-9" />
              </Field>
            </div>
            <Field label="Guest message">
              <Input value={form.ramadan_message || ""} onChange={(e) => setField("ramadan_message", e.target.value)} className="h-9" />
            </Field>
          </Card>
        </div>
        </TabsContent>

        <TabsContent value="qr" className="mt-0 space-y-4 focus-visible:ring-0">
        <Card className="p-5 overflow-hidden">
          <SectionTitle>{t("qrMenu.setupTitle")}</SectionTitle>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(220px,280px)] gap-6 items-start">
            <div className="min-w-0 space-y-3">
              <Toggle
                label={t("qrMenu.enableMenu")}
                hint={t("qrMenu.enableMenuHint")}
                checked={Boolean(form.qr_menu_enabled)}
                onCheckedChange={(v) => setField("qr_menu_enabled", v)}
              />
              <Toggle
                label={t("qrMenu.enableSelfOrder")}
                hint={t("qrMenu.enableSelfOrderHint")}
                checked={form.qr_self_order_enabled !== false}
                onCheckedChange={(v) => setField("qr_self_order_enabled", v)}
              />
              <Field label={t("qrMenu.theme")}>
                <Select
                  value={form.qr_menu_theme || "warm_spice"}
                  onValueChange={(v) => setField("qr_menu_theme", v)}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QR_MENU_THEMES.map((th) => (
                      <SelectItem key={th.id} value={th.id}>
                        {th.label} — {th.hint}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("qrMenu.tagline")}>
                <Input
                  value={form.qr_menu_tagline || ""}
                  onChange={(e) => setField("qr_menu_tagline", e.target.value)}
                  className="h-9"
                  placeholder="Scan for today’s menu"
                />
              </Field>
              <Field label={t("qrMenu.logoUrl")}>
                <Input
                  value={form.qr_menu_logo_url || ""}
                  onChange={(e) => setField("qr_menu_logo_url", e.target.value)}
                  className="h-9"
                  placeholder="https://…"
                />
              </Field>
              {form.qr_menu_enabled && branchCode ? (
                <div className="rounded-xl border border-[#E8D5C8] bg-[#FFF7F0] p-3 space-y-2">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-brand-brown/60">
                    {t("qrMenu.browseLink")}
                  </p>
                  <p className="text-[12px] font-mono break-all text-brand-brown">
                    {qrMenuBrowseUrl(branchCode)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8"
                      onClick={() => {
                        navigator.clipboard.writeText(qrMenuBrowseUrl(branchCode));
                        toast.success(t("qrMenu.linkCopied"));
                      }}
                    >
                      <Copy size={13} className="mr-1" />
                      {t("qrMenu.copyLink")}
                    </Button>
                    <Button type="button" size="sm" variant="outline" className="h-8" asChild>
                      <a href={qrMenuBrowseUrl(branchCode)} target="_blank" rel="noreferrer">
                        <ExternalLink size={13} className="mr-1" />
                        {t("qrMenu.openMenu")}
                      </a>
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="h-8 bg-brand-orange hover:bg-[#E05E10]"
                      onClick={() => printQrMenuCards()}
                    >
                      <Printer size={13} className="mr-1" />
                      {t("qrMenu.printSample")}
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-500">
                    {t("qrMenu.selfOrderHint", { code: branchCode })}
                  </p>
                </div>
              ) : null}
            </div>
            <div className="flex flex-col items-center gap-2 lg:sticky lg:top-4">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 w-full text-center lg:text-left">
                {t("qrMenu.cardPreview")}
              </p>
              <div className="w-full max-w-[260px]">
                <QrMenuCardPreview
                  restaurantName={branchName}
                  tagline={form.qr_menu_tagline}
                  theme={form.qr_menu_theme || "warm_spice"}
                  logoUrl={form.qr_menu_logo_url || undefined}
                  branchCode={branchCode || "MAIN"}
                />
              </div>
            </div>
          </div>
          {branchCode ? (
            <QrMenuCardPrint
              restaurantName={branchName}
              tagline={form.qr_menu_tagline}
              theme={form.qr_menu_theme || "warm_spice"}
              logoUrl={form.qr_menu_logo_url || undefined}
              branchCode={branchCode}
              count={1}
            />
          ) : null}
        </Card>
        </TabsContent>

        <TabsContent value="pos" className="mt-0 space-y-4 focus-visible:ring-0">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <Card className="p-5">
            <SectionTitle>Kitchen Display</SectionTitle>
            <Toggle label="High-contrast KDS mode (toggle on KDS screen too)" checked={Boolean(form.kds_high_contrast)} onCheckedChange={(v) => setField("kds_high_contrast", v)} />
            <Toggle label="Sound alerts on new KOT" checked={form.kds_sound_alerts !== false} onCheckedChange={(v) => setField("kds_sound_alerts", v)} />
          </Card>

          <Card className="p-5">
            <SectionTitle>POS Preferences</SectionTitle>
            <Toggle label="Show item images in POS" hint="Display product images in the POS grid" checked={form.show_item_images} onCheckedChange={(v) => setField("show_item_images", v)} />
            <Toggle label="Auto-print receipt on payment" checked={form.auto_print_receipt} onCheckedChange={(v) => setField("auto_print_receipt", v)} />
            <Toggle label="Require customer for dine-in orders" checked={form.require_customer_dine_in} onCheckedChange={(v) => setField("require_customer_dine_in", v)} />
            <Toggle label="Allow open price entry" checked={form.allow_open_price} onCheckedChange={(v) => setField("allow_open_price", v)} />
            <Toggle label="Show running order numbers daily" checked={form.daily_order_numbers} onCheckedChange={(v) => setField("daily_order_numbers", v)} />
            <Toggle label="Enable Fast Menu shortcuts (F1–F12)" checked={form.fast_menu_shortcuts} onCheckedChange={(v) => setField("fast_menu_shortcuts", v)} />
          </Card>

          <Card className="p-5">
            <SectionTitle>Kitchen / KOT Settings</SectionTitle>
            <Toggle label="Auto-send KOT on item add" hint="When off, use SEND KOT on POS to fire kitchen tickets" checked={form.auto_send_kot} onCheckedChange={(v) => setField("auto_send_kot", v)} />
            <Toggle label="Group KOT by station" checked={form.group_kot_by_station} onCheckedChange={(v) => setField("group_kot_by_station", v)} />
            <Toggle label="Allow KOT cancellation by manager only" checked={form.kot_cancel_manager_only} onCheckedChange={(v) => setField("kot_cancel_manager_only", v)} />
            <Toggle label="Show kitchen notes on KDS" checked={form.show_kitchen_notes_kds} onCheckedChange={(v) => setField("show_kitchen_notes_kds", v)} />
            <Toggle label="Alert if order open > 15 minutes" checked={form.alert_open_order_minutes >= 15} onCheckedChange={(v) => setField("alert_open_order_minutes", v ? 15 : 0)} />
          </Card>
        </div>

        <Card className="p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <SectionTitle className="!mb-0">Station → Printer Routing</SectionTitle>
            <Button
              size="sm"
              className="h-8"
              onClick={() => saveStationsMutation.mutate()}
              disabled={saveStationsMutation.isPending || stationsLoading || !stationDraft.length}
            >
              {saveStationsMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Save routing
            </Button>
          </div>
          <p className="text-[11.5px] text-slate-500 mb-4">
            Maps Hot / Cold / Bar stations to kitchen printers. LAN printers print directly; USB printers use browser fallback.
          </p>
          {stationsLoading ? (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading stations…
            </div>
          ) : (
            <div className="space-y-3">
              {stationDraft.map((station, idx) => (
                <div key={station.code} className="grid grid-cols-1 sm:grid-cols-[1fr_1.4fr_auto] gap-3 items-end border-b pb-3 last:border-b-0">
                  <Field label={station.name}>
                    <Input value={station.code.toUpperCase()} readOnly className="h-9 bg-slate-50 font-mono text-[12px]" />
                  </Field>
                  <Field label="Kitchen printer">
                    <Select
                      value={station.printer_id || "__none__"}
                      onValueChange={(v) => {
                        setStationDraft((prev) => prev.map((s, i) => (
                          i === idx ? { ...s, printer_id: v === "__none__" ? "" : v } : s
                        )));
                      }}
                    >
                      <SelectTrigger className="h-9"><SelectValue placeholder="— No printer —" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— No printer —</SelectItem>
                        {kitchenPrinters.map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.code} — {p.name} ({p.connection}{p.ip_address ? ` ${p.ip_address}` : ""})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Toggle
                    label="Active"
                    checked={station.is_active}
                    onCheckedChange={(v) => {
                      setStationDraft((prev) => prev.map((s, i) => (i === idx ? { ...s, is_active: v } : s)));
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-5">
          <div className="flex items-center justify-between gap-3 mb-3">
            <SectionTitle>Fast Menu Slots (F1–F12)</SectionTitle>
            <Button
              size="sm"
              variant="outline"
              className="h-8"
              data-testid="fast-menu-save"
              onClick={() => saveFastMenuMutation.mutate()}
              disabled={saveFastMenuMutation.isPending || fastMenuLoading}
            >
              {saveFastMenuMutation.isPending ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
              Save Fast Menu
            </Button>
          </div>
          <p className="text-[11px] text-slate-500 mb-3">
            F8 and F10 are reserved for KOT notes and bill reprint. Assign POS items to the other keys.
          </p>
          {fastMenuLoading ? (
            <div className="h-24 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {slotDraft.map((slot, idx) => {
                const reserved = slot.slot_key === "F8" || slot.slot_key === "F10";
                return (
                  <Field key={slot.slot_key} label={slot.slot_key}>
                    {reserved ? (
                      <Input
                        value={slot.slot_key === "F8" ? "KOT NOTE (reserved)" : "REPRINT (reserved)"}
                        disabled
                        className="h-9 text-slate-500"
                      />
                    ) : (
                      <Select
                        value={slot.menu_item_id || "__empty__"}
                        onValueChange={(v) => {
                          setSlotDraft((prev) => prev.map((s, i) => (
                            i === idx ? { ...s, menu_item_id: v === "__empty__" ? "" : v } : s
                          )));
                        }}
                      >
                        <SelectTrigger className="h-9"><SelectValue placeholder="— Empty —" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__empty__">— Empty —</SelectItem>
                          {menuItems.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>
                              {m.code} — {m.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </Field>
                );
              })}
            </div>
          )}
        </Card>
        </TabsContent>

        <TabsContent value="delivery" className="mt-0 space-y-4 focus-visible:ring-0">
          <Card className="p-5 max-w-2xl">
            <SectionTitle>Delivery Settings</SectionTitle>
            <Toggle label="Auto-assign nearest driver" checked={form.auto_assign_driver} onCheckedChange={(v) => setField("auto_assign_driver", v)} />
            <Toggle label="Notify customer on dispatch" checked={form.notify_customer_dispatch} onCheckedChange={(v) => setField("notify_customer_dispatch", v)} />
            <Toggle label="Allow COD" checked={form.allow_cod} onCheckedChange={(v) => setField("allow_cod", v)} />
            <Toggle label="Require driver signature on delivery" checked={form.require_driver_signature} onCheckedChange={(v) => setField("require_driver_signature", v)} />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Field label="Minimum Order Value">
                <Input value={form.min_order_value} onChange={(e) => setField("min_order_value", Number(e.target.value))} type="number" className="h-9 text-right font-mono" />
              </Field>
              <Field label="Delivery Charge ">
                <Input value={form.delivery_charge} onChange={(e) => setField("delivery_charge", Number(e.target.value))} type="number" className="h-9 text-right font-mono" />
              </Field>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}
